import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Campaign as CampaignIcon,
  Chat as ChatIcon,
  CompareArrows as CompareArrowsIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PlayArrow as PlayArrowIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ConversationViewer from '../components/ConversationViewer';
import AnnouncementAdminAttributes from '../components/AnnouncementAdminAttributes';
import AnnouncementAdminEditDialog from '../components/AnnouncementAdminEditDialog';
import AnnouncementAdminGallery from '../components/AnnouncementAdminGallery';
import apiService from '../services/api';
import type {
  AdminAnnouncementConversation,
  AdminAnnouncementDetail,
  AdminAnnouncementUpdatePayload,
} from '../types/announcementAdmin';
import type { Conversation } from '../types/moderation';

const TYPE_LABELS: Record<string, string> = {
  lost: 'Perdu',
  found: 'Trouvé',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  resolved: 'Résolue',
  closed: 'Fermée',
};

const MATCH_STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  confirmed: 'Confirmé',
  rejected: 'Rejeté',
  resolved: 'Résolu',
};

const SHARE_CHANNEL_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  x: 'X (Twitter)',
  whatsapp: 'WhatsApp',
  copy: 'Copie du lien',
  native: 'Partage natif (mobile)',
};

function userFullName(user: { first_name: string | null; last_name: string | null; email: string }) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.email;
}

function toConversationViewerModel(
  c: AdminAnnouncementConversation,
  announcement: Pick<AdminAnnouncementDetail, 'id' | 'title' | 'type'>,
): Conversation {
  return {
    id: c.id,
    announcement: {
      id: announcement.id,
      title: announcement.title,
      type: announcement.type,
    },
    user1: {
      id: c.user1.id,
      email: c.user1.email,
      firstName: c.user1.first_name ?? undefined,
      lastName: c.user1.last_name ?? undefined,
      fullName: userFullName(c.user1),
    },
    user2: {
      id: c.user2.id,
      email: c.user2.email,
      firstName: c.user2.first_name ?? undefined,
      lastName: c.user2.last_name ?? undefined,
      fullName: userFullName(c.user2),
    },
    messages_count: c.messages_count,
    created_at: c.created_at ?? '',
    updated_at: c.updated_at ?? '',
    is_active: c.is_active,
    last_message: c.last_message
      ? {
          id: c.last_message.id,
          content: c.last_message.content,
          is_read: true,
          created_at: c.last_message.created_at ?? '',
          sender: {
            id: 0,
            email: c.last_message.sender_email ?? '',
            fullName: c.last_message.sender_email ?? '',
            is_moderator: false,
          },
          recipient: {
            id: 0,
            email: '',
            fullName: '',
          },
        }
      : undefined,
  };
}

const AnnouncementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const announcementId = Number(id);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-announcement', announcementId],
    queryFn: () => apiService.getAdminAnnouncement(announcementId),
    enabled: Number.isFinite(announcementId) && announcementId > 0,
  });

  const announcement = data?.data;

  const statusMutation = useMutation({
    mutationFn: ({ status }: { status: 'active' | 'closed' }) =>
      apiService.patchAdminAnnouncementStatus(announcementId, status),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-announcement', announcementId] });
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
      setSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Impossible de modifier le statut.', severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: AdminAnnouncementUpdatePayload) =>
      apiService.updateAdminAnnouncement(announcementId, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-announcement', announcementId] });
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
      setEditDialogOpen(false);
      navigate('/announcements', { state: { flashMessage: res.message } });
    },
    onError: (error: unknown) => {
      const details =
        (error as { response?: { data?: { details?: string[]; error?: string } } })?.response?.data
          ?.details;
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (Array.isArray(details) && details.length > 0 ? details.join(' ') : null)
        || 'Impossible de mettre à jour l’annonce.';
      setSnackbar({ open: true, message, severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteAdminAnnouncement(announcementId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
      setSnackbar({ open: true, message: res.message, severity: 'success' });
      navigate('/announcements');
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Suppression impossible.', severity: 'error' });
    },
  });

  const matchStatusChips = useMemo(() => {
    if (!announcement?.matches_by_status) return [];
    return Object.entries(announcement.matches_by_status).filter(([, count]) => count > 0);
  }, [announcement]);

  if (selectedConversation) {
    return (
      <ConversationViewer
        conversation={selectedConversation}
        onBack={() => setSelectedConversation(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !announcement) {
    return (
      <Box>
        <Alert severity="error">Annonce introuvable ou erreur de chargement.</Alert>
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate('/announcements')}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/announcements')} variant="outlined">
          Retour
        </Button>
        <Breadcrumbs>
          <Link component={RouterLink} to="/announcements" underline="hover" color="inherit">
            Annonces
          </Link>
          <Typography color="text.primary">#{announcement.id}</Typography>
        </Breadcrumbs>
      </Stack>

      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <CampaignIcon color="primary" />
        <Typography variant="h4" component="h1">
          {announcement.title}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Chip label={TYPE_LABELS[announcement.type] ?? announcement.type} color="primary" variant="outlined" />
        <Chip label={STATUS_LABELS[announcement.status] ?? announcement.status} />
        {announcement.reference_code && <Chip label={announcement.reference_code} variant="outlined" />}
        {announcement.location_provider && (
          <Chip label={`Partenaire : ${announcement.location_provider.name}`} variant="outlined" />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setEditDialogOpen(true)}
        >
          Modifier
        </Button>
        {announcement.status === 'active' ? (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            disabled={statusMutation.isPending}
            onClick={() => statusMutation.mutate({ status: 'closed' })}
          >
            Désactiver
          </Button>
        ) : announcement.status === 'closed' ? (
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<PlayArrowIcon />}
            disabled={statusMutation.isPending}
            onClick={() => statusMutation.mutate({ status: 'active' })}
          >
            Réactiver
          </Button>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={deleteMutation.isPending}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Supprimer
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Photo
              </Typography>
              <AnnouncementAdminGallery
                thumbnailUrl={announcement.thumbnail_url}
                imageUrls={announcement.image_urls}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Détails
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {announcement.description || '—'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Catégorie
                  </Typography>
                  <Typography variant="body2">{announcement.category?.name ?? '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Lieu
                  </Typography>
                  <Typography variant="body2">
                    {announcement.location?.full_address ||
                      [announcement.location?.city, announcement.location?.postal_code].filter(Boolean).join(' ') ||
                      announcement.specific_location ||
                      '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date perte/trouvaille
                  </Typography>
                  <Typography variant="body2">{announcement.lost_found_date ?? '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Récompense
                  </Typography>
                  <Typography variant="body2">{announcement.reward ?? '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Créée le
                  </Typography>
                  <Typography variant="body2">{formatDate(announcement.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Mise à jour
                  </Typography>
                  <Typography variant="body2">{formatDate(announcement.updated_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Résolue le
                  </Typography>
                  <Typography variant="body2">{formatDate(announcement.resolved_at)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Attributs
              </Typography>
              <AnnouncementAdminAttributes
                categoryId={announcement.category?.id}
                objectAttributes={announcement.object_attributes}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon color="action" />
                <Typography variant="h6">Utilisateur</Typography>
              </Box>
              {announcement.user ? (
                <>
                  <Typography variant="body1" fontWeight={600}>
                    {userFullName(announcement.user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {announcement.user.email}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/users/${announcement.user!.id}`)}
                  >
                    Voir le profil
                  </Button>
                </>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Synthèse
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CompareArrowsIcon fontSize="small" />
                    <Typography variant="body2">Matchings</Typography>
                  </Box>
                  <Chip size="small" label={announcement.matches_count} color="primary" />
                </Box>
                {matchStatusChips.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {matchStatusChips.map(([statusKey, count]) => (
                      <Chip
                        key={statusKey}
                        size="small"
                        variant="outlined"
                        label={`${MATCH_STATUS_LABELS[statusKey] ?? statusKey} : ${count}`}
                      />
                    ))}
                  </Stack>
                )}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <ChatIcon fontSize="small" />
                    <Typography variant="body2">Conversations</Typography>
                  </Box>
                  <Chip size="small" label={announcement.conversations_count} color="secondary" />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <ShareIcon fontSize="small" />
                    <Typography variant="body2">Partages</Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={announcement.share_summary?.total ?? 0}
                    color={(announcement.share_summary?.total ?? 0) > 0 ? 'info' : 'default'}
                  />
                </Box>
                {(announcement.share_summary?.channels?.length ?? 0) > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {announcement.share_summary.channels.map((channel) => (
                      <Chip
                        key={channel}
                        size="small"
                        variant="outlined"
                        label={`${SHARE_CHANNEL_LABELS[channel] ?? channel} : ${announcement.share_summary.counts_by_channel[channel] ?? 0}`}
                      />
                    ))}
                  </Stack>
                )}
                {announcement.share_summary?.last_shared_at && (
                  <Typography variant="caption" color="text.secondary">
                    Dernier partage : {formatDate(announcement.share_summary.last_shared_at)}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ShareIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Historique des partages ({announcement.share_events?.length ?? 0})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chaque ligne correspond à un clic de partage par le créateur de l&apos;annonce (intention, pas publication
            confirmée sur le réseau).
          </Typography>
          {(announcement.share_events?.length ?? 0) === 0 ? (
            <Alert severity="info">Aucun partage enregistré pour cette annonce.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Réseau</TableCell>
                    <TableCell>Utilisateur</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {announcement.share_events.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>{formatDate(event.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={SHARE_CHANNEL_LABELS[event.channel] ?? event.channel}
                        />
                      </TableCell>
                      <TableCell>
                        {event.user ? userFullName(event.user) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <CompareArrowsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Matchings ({announcement.matches.length})
          </Typography>
          {announcement.matches.length === 0 ? (
            <Alert severity="info">Aucun matching pour cette annonce.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Autre annonce / Objet Pro</TableCell>
                    <TableCell>Créé le</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {announcement.matches.map((match) => (
                    <TableRow key={match.id}>
                      <TableCell>{match.id}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={MATCH_STATUS_LABELS[match.status] ?? match.status}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{match.match_score ?? '—'}</TableCell>
                      <TableCell>
                        {match.other_announcement ? (
                          <Box>
                            <Link
                              component={RouterLink}
                              to={`/announcements/${match.other_announcement.id}`}
                              underline="hover"
                            >
                              #{match.other_announcement.id} — {match.other_announcement.title}
                            </Link>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {TYPE_LABELS[match.other_announcement.type] ?? match.other_announcement.type}
                              {match.other_announcement.user_email ? ` · ${match.other_announcement.user_email}` : ''}
                            </Typography>
                          </Box>
                        ) : match.target_found_object ? (
                          <Box>
                            <Typography variant="body2">{match.target_found_object.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Objet Pro · {match.target_found_object.reference_code ?? `#${match.target_found_object.id}`}
                            </Typography>
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(match.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ChatIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Conversations ({announcement.conversations.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ouvrez une conversation pour lire les messages et intervenir en modérateur Sherlook.
          </Typography>
          {announcement.conversations.length === 0 ? (
            <Alert severity="info">Aucune conversation liée à cette annonce.</Alert>
          ) : (
            <Grid container spacing={2}>
              {announcement.conversations.map((conversation) => (
                <Grid item xs={12} md={6} key={conversation.id}>
                  <Card
                    variant="outlined"
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() =>
                      setSelectedConversation(toConversationViewerModel(conversation, announcement))
                    }
                  >
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Conversation #{conversation.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {userFullName(conversation.user1)} ↔ {userFullName(conversation.user2)}
                      </Typography>
                      {conversation.last_message && (
                        <Typography variant="body2" noWrap sx={{ mb: 1 }}>
                          {conversation.last_message.content}
                        </Typography>
                      )}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Chip size="small" label={`${conversation.messages_count} message(s)`} />
                        <Typography variant="caption" color="text.secondary">
                          {conversation.updated_at
                            ? new Date(conversation.updated_at).toLocaleDateString('fr-FR')
                            : '—'}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <AnnouncementAdminEditDialog
        open={editDialogOpen}
        announcement={announcement}
        saving={updateMutation.isPending}
        onClose={() => !updateMutation.isPending && setEditDialogOpen(false)}
        onSave={(payload) => updateMutation.mutate(payload)}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !deleteMutation.isPending && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer l&apos;annonce</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer définitivement l&apos;annonce <strong>#{announcement.id}</strong> —{' '}
            <strong>{announcement.title}</strong> ?
            <br />
            <br />
            Matchings, conversations, messages et images associés seront supprimés (y compris objet
            trouvé partenaire / miroir FO-CONTACT le cas échéant). Action irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
            Annuler
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnnouncementDetailPage;
